


import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { translations } from "./translations";

const LanguageContext = createContext(null);

const SUPPORTED_LANGUAGES = ["de", "en"];
const DEFAULT_LANGUAGE = "en";
const STORAGE_KEY = "vaultquest_language";

function detectBrowserLanguage() {
  const browserLang =
    navigator.language ||
    (Array.isArray(navigator.languages) && navigator.languages[0]) ||
    DEFAULT_LANGUAGE;

  const normalized = String(browserLang).toLowerCase();

  if (normalized.startsWith("de")) return "de";
  if (normalized.startsWith("en")) return "en";

  return DEFAULT_LANGUAGE;
}

function getInitialLanguage() {
  const urlLang = new URLSearchParams(
    window.location.search
  ).get("lang");

  if (
    urlLang &&
    SUPPORTED_LANGUAGES.includes(urlLang)
  ) {
    return urlLang;
  }

  const saved = localStorage.getItem(STORAGE_KEY);

  if (saved && SUPPORTED_LANGUAGES.includes(saved)) {
    return saved;
  }

  return detectBrowserLanguage();
}

function getNestedValue(obj, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc && typeof acc === "object" && key in acc) {
      return acc[key];
    }
    return undefined;
  }, obj);
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const setLanguage = useCallback(async (nextLanguage) => {
    if (!SUPPORTED_LANGUAGES.includes(nextLanguage)) return;

    setLanguageState(nextLanguage);
    localStorage.setItem(STORAGE_KEY, nextLanguage);

    const uid = auth.currentUser?.uid;
    if (!uid) return;

    try {
      await setDoc(
        doc(db, "users", uid),
        { language: nextLanguage },
        { merge: true }
      );
    } catch (error) {
      console.error("Failed to save language to Firestore:", error?.code || error?.message || "unknown");
    }
  }, []);

  const t = useCallback((key, varsOrFallback = key, maybeFallback) => {
    const vars =
      varsOrFallback && typeof varsOrFallback === "object" && !Array.isArray(varsOrFallback)
        ? varsOrFallback
        : {};

    const fallback =
      varsOrFallback && typeof varsOrFallback === "object" && !Array.isArray(varsOrFallback)
        ? maybeFallback ?? key
        : varsOrFallback ?? key;

    const currentTranslations = translations[language] || translations[DEFAULT_LANGUAGE];
    const fallbackTranslations = translations[DEFAULT_LANGUAGE];

    let value =
      getNestedValue(currentTranslations, key) ??
      getNestedValue(fallbackTranslations, key) ??
      fallback;

    if (typeof value !== "string") return value;

    Object.keys(vars).forEach((varKey) => {
      value = value.replace(new RegExp(`{{\\s*${varKey}\\s*}}`, "g"), String(vars[varKey]));
    });

    return value;
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      supportedLanguages: SUPPORTED_LANGUAGES,
    }),
    [language, setLanguage, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }

  return context;
}
